import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../store';
import { API_BASE_URL } from '../utils/api';
import { isAdminRole } from '../utils/permissions';
import { Search, ChevronLeft, ChevronRight, Filter, Upload, Clipboard, Download } from 'lucide-react';

interface VocabItem {
  vocab_id: string;
  hanzi: string;
  pinyin: string;
  type: string;
  En_meaning: string;
  Id_meaning: string;
  Chapter: string;
  Text: string;
  Sentence: string;
  Sentence_meaning: string;
  level: number;
}

const VocabLibrary: React.FC = () => {
  const { token, user } = useAuthStore();
  const [version, setVersion] = useState<'2.0' | '3.0'>('3.0');
  const [level, setLevel] = useState<number | ''>('');
  const [chapter, setChapter] = useState<string>('');
  const [textFilter, setTextFilter] = useState<string>('');
  const [search, setSearch] = useState('');
  const [data, setData] = useState<VocabItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [uploading, setUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState('');
  const [showPasteDialog, setShowPasteDialog] = useState(false);

  const fetchVocab = async () => {
    setLoading(true);
    try {
      const offset = (page - 1) * limit;
      const params = new URLSearchParams({
        version,
        limit: String(limit),
        offset: String(offset),
      });
      if (level) params.append('level', String(level));
      if (chapter) params.append('chapter', chapter);
      if (textFilter) params.append('text', textFilter);
      if (search) params.append('search', search);

      const res = await fetch(`${API_BASE_URL}/vocab?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const json = await res.json();
      if (json.success) {
        setData(json.data);
        setTotal(json.total);
      }
    } catch (error) {
      console.error('Failed to fetch vocab', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVocab();
  }, [version, level, chapter, textFilter, page, search]); // Debounce search in real app, simplified here

  const totalPages = Math.ceil(total / limit);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setUploadMessage('');
    
    try {
      const text = await file.text();
      let content;
      try {
        content = JSON.parse(text);
      } catch {
        setUploadMessage('Invalid JSON file');
        setUploading(false);
        return;
      }
      
      // Support raw array or object with content/items property
      const items = Array.isArray(content) ? content : (content.content || content.items || []);

      const res = await fetch(`${API_BASE_URL}/vocab/upload-json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          version,
          content: items
        }),
      });

      const json = await res.json();
      if (json.success) {
        setUploadMessage(`Success: ${json.message}`);
        fetchVocab(); // Refresh list
      } else {
        setUploadMessage(`Error: ${json.message}`);
      }
    } catch (err) {
      setUploadMessage('Upload failed');
    } finally {
      setUploading(false);
      // Reset input
      e.target.value = '';
    }
  };

  const handlePasteImport = async (content: any[], targetVersion: '2.0' | '3.0') => {
    setUploading(true);
    setUploadMessage('');
    try {
      const res = await fetch(`${API_BASE_URL}/vocab/upload-json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          version: targetVersion,
          content: content
        }),
      });

      const json = await res.json();
      if (json.success) {
        setUploadMessage(`Success: ${json.message}`);
        fetchVocab();
        setShowPasteDialog(false);
      } else {
        setUploadMessage(`Error: ${json.message}`);
      }
    } catch (err) {
      setUploadMessage('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleExport = async () => {
    setUploading(true);
    setUploadMessage('');
    try {
      const res = await fetch(`${API_BASE_URL}/vocab?version=${version}&limit=all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const json = await res.json();
      if (json.success) {
        const blob = new Blob([JSON.stringify(json.data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `hsk-${version}-vocab-local-db.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        setUploadMessage('Export successful');
      } else {
        setUploadMessage(`Export failed: ${json.message}`);
      }
    } catch (e) {
      setUploadMessage('Export failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Vocab Library</h1>
        <div className="flex items-center space-x-4">
            {isAdminRole(user?.role) && (
              <>
                {uploadMessage && (
                    <span className={`text-sm ${uploadMessage.startsWith('Error') || uploadMessage.includes('failed') ? 'text-red-600' : 'text-green-600'}`}>
                        {uploadMessage}
                    </span>
                )}
                <button
                  onClick={handleExport}
                  disabled={uploading}
                  className={`inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Local-db
                </button>
                <button
                  onClick={() => setShowPasteDialog(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  <Clipboard className="w-4 h-4 mr-2" />
                  Batch Paste
                </button>
                <label className={`cursor-pointer inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}>
                    <Upload className="w-4 h-4 mr-2" />
                    {uploading ? 'Uploading...' : 'Import JSON'}
                    <input 
                        type="file" 
                        className="hidden" 
                        accept=".json" 
                        onChange={handleFileUpload} 
                        disabled={uploading}
                    />
                </label>
              </>
            )}
        </div>
      </div>

      <div className="bg-white shadow rounded-lg p-6 space-y-4">
        {/* Controls */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex items-center space-x-2">
            <span className="font-medium text-gray-700">Chapter:</span>
            <input
              type="text"
              placeholder="e.g. 1"
              value={chapter}
              onChange={(e) => { setChapter(e.target.value); setPage(1); }}
              className="w-20 border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div className="flex items-center space-x-2">
             <span className="font-medium text-gray-700">Version:</span>
             <div className="flex border rounded overflow-hidden">
                <button 
                  onClick={() => { setVersion('3.0'); setPage(1); }}
                  className={`px-4 py-2 ${version === '3.0' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  HSK 3.0
                </button>
                <button 
                  onClick={() => { setVersion('2.0'); setPage(1); }}
                  className={`px-4 py-2 ${version === '2.0' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  HSK 2.0
                </button>
             </div>
          </div>

          <div className="flex items-center space-x-2">
            <span className="font-medium text-gray-700">Level:</span>
            <select 
              value={level} 
              onChange={(e) => { setLevel(e.target.value ? Number(e.target.value) : ''); setPage(1); }}
              className="border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Levels</option>
              {[1, 2, 3, 4, 5, 6].map(l => (
                <option key={l} value={l}>HSK {l}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center space-x-2">
            <span className="font-medium text-gray-700">Text:</span>
            <input
              type="text"
              placeholder="e.g. 1"
              value={textFilter}
              onChange={(e) => { setTextFilter(e.target.value); setPage(1); }}
              className="w-20 border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div className="flex-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search Hanzi, Pinyin, or Meaning..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Level</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Chapter</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Text</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hanzi</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pinyin</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">English</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Indonesian</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Example</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500">Loading...</td>
                </tr>
              ) : data.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500">No vocabulary found.</td>
                </tr>
              ) : (
                data.map((item) => (
                  <tr key={item.vocab_id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">HSK {item.level}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.Chapter}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.Text}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-lg">{item.hanzi}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.pinyin}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.type}</td>
                    <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate" title={item.En_meaning}>{item.En_meaning}</td>
                    <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate" title={item.Id_meaning}>{item.Id_meaning}</td>
                    <td className="px-6 py-4 text-sm text-gray-500 max-w-md">
                      {item.Sentence && (
                        <div>
                          <div className="text-gray-900">{item.Sentence}</div>
                          <div className="text-gray-400 text-xs">{item.Sentence_meaning}</div>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3 sm:px-6">
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">{(page - 1) * limit + 1}</span> to <span className="font-medium">{Math.min(page * limit, total)}</span> of <span className="font-medium">{total}</span> results
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Previous</span>
                  <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                </button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages || totalPages === 0}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Next</span>
                  <ChevronRight className="h-5 w-5" aria-hidden="true" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      </div>

      {showPasteDialog && (
        <BulkPasteDialog
          onCancel={() => setShowPasteDialog(false)}
          onImport={handlePasteImport}
          loading={uploading}
          defaultVersion={version}
        />
      )}
    </div>
  );
};

export default VocabLibrary;

const BulkPasteDialog: React.FC<{ onCancel: () => void; onImport: (items: any[], targetVersion: '2.0' | '3.0') => void; loading?: boolean; defaultVersion: '2.0' | '3.0' }> = ({ onCancel, onImport, loading, defaultVersion }) => {
  const [text, setText] = useState('');
  const [error, setError] = useState('');
  const [targetVersion, setTargetVersion] = useState<'2.0' | '3.0'>(defaultVersion);

  const parseItems = (): any[] => {
    try {
      // Pre-process to fix NaN which is invalid JSON
      // Use a regex that preserves spacing or just replaces the token.
      // Since we parse immediately after, spacing doesn't strictly matter for validity,
      // but let's be safe. simple replacement of ": NaN" or ":NaN" with ": null".
      const sanitizedText = text.replace(/:\s*NaN\b/g, ': null');
      const content = JSON.parse(sanitizedText);
      
      // Support raw array or object with content/items property
      const items = Array.isArray(content) ? content : (content.content || content.items || []);
      
      if (!Array.isArray(items)) {
        setError('Parsed content is not an array');
        return [];
      }
      return items;
    } catch (e: any) {
      setError('JSON Parse Error: ' + e.message);
      return [];
    }
  };

  const example = JSON.stringify([
    {
      "vocab_id": "v3-001",
      "hanzi": "爱",
      "pinyin": "ài",
      "type": "verb",
      "En_meaning": "to love",
      "Id_meaning": "cinta",
      "level": 1,
      "Chapter": "1",
      "Text": 1,
      "Sentence": "我爱你",
      "Sentence_meaning": "I love you"
    }
  ], null, 2);

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
      <div className="bg-white rounded shadow p-6 w-full max-w-2xl">
        <h3 className="text-lg font-semibold mb-4">Batch Paste JSON</h3>
        <div className="mb-3 text-sm text-gray-600">
          Paste your JSON array here. Any "NaN" values will be automatically converted to null.
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Target Version:</label>
          <div className="flex space-x-4">
            <label className="inline-flex items-center cursor-pointer">
              <input 
                type="radio" 
                className="form-radio h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500" 
                name="targetVersion" 
                value="3.0" 
                checked={targetVersion === '3.0'} 
                onChange={() => setTargetVersion('3.0')}
              />
              <span className="ml-2 text-sm text-gray-700">HSK 3.0</span>
            </label>
            <label className="inline-flex items-center cursor-pointer">
              <input 
                type="radio" 
                className="form-radio h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500" 
                name="targetVersion" 
                value="2.0" 
                checked={targetVersion === '2.0'} 
                onChange={() => setTargetVersion('2.0')}
              />
              <span className="ml-2 text-sm text-gray-700">HSK 2.0</span>
            </label>
          </div>
        </div>

        <div className="flex items-center mb-2 space-x-3">
          <button onClick={() => setText(example)} className="px-2 py-1 border rounded text-sm hover:bg-gray-50">
            Load Example
          </button>
          <button onClick={() => setText('')} className="px-2 py-1 border rounded text-sm hover:bg-gray-50">
            Clear
          </button>
        </div>
        <textarea 
          value={text} 
          onChange={e => { setText(e.target.value); setError(''); }} 
          rows={12} 
          className="w-full border rounded px-3 py-2 font-mono text-sm focus:ring-indigo-500 focus:border-indigo-500" 
          placeholder="Paste JSON here..."
        />
        {error && <div className="mt-2 text-red-600 text-sm">{error}</div>}
        <div className="flex justify-end space-x-2 mt-4">
          <button onClick={onCancel} className="px-3 py-2 border rounded hover:bg-gray-50">Cancel</button>
          <button 
            onClick={() => { 
              const items = parseItems(); 
              if (items.length === 0 && !error) { 
                setError('Content is empty or invalid'); 
                return; 
              } 
              if (items.length > 0) onImport(items, targetVersion); 
            }} 
            className={`px-3 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={loading}
          >
            {loading ? 'Importing...' : 'Import'}
          </button>
        </div>
      </div>
    </div>
  );
};
